// BottomNavigation 选中态 Lottie 图标的「运行时主题着色」层。
//
// 选中态图标的青绿色（teal ≈ #056A80）被烘焙在 lottieData.ts 的渐变填充（gf）
// 色标里，scss 的 var() 够不着。这里在动画数据挂载前做一次非破坏性深拷贝，
// 把其中的 teal 色标替换成当前主题的 --color-brand-primary，使选中图标跟随主题色。
//
// 只改渐变色标的 r/g/b 三个分量，绝不动色标位置、透明度尾段与图形路径，
// 因此不会破坏动画的形状 / 时序 / 透明度。

// 烘焙在动画数据里的青绿色，归一化 RGB（对应 lottieData.ts 中的 0.02,0.416,0.502）。
// 这是需要被「找出并替换」的锚点色，不是设计 token，故不走 var()。
const BAKED_TEAL: readonly [number, number, number] = [0.02, 0.416, 0.502];

// 未选中态图标的图形烘焙成纯黑（lottieData 的 fl 实心填充 c=[0,0,0]，非渐变）。
// 深色主题下黑图标压深底栏(--color-bg-secondary)看不清，故把纯黑实心填充改成
// 未选中图标色（对齐同态 label 的 --color-text-secondary，随主题翻：浅套深灰/深套浅暖灰）。
const BAKED_BLACK: readonly [number, number, number] = [0, 0, 0];

// home 图标「门洞」（door / door 2 图层）烘焙的淡灰紫实心填充，归一化 RGB
// （对应 lottieData.ts 中的 0.8,0.7961,0.8392）。这是写死、不跟主题走的锚点色，
// 需被识别并改成导航底色，使拱门在任意主题下都读作「镂空门洞」而非固定灰白。
const BAKED_DOOR: readonly [number, number, number] = [0.8, 0.7961, 0.8392];

// 单通道匹配容差：teal 三档色标完全相同，用较小容差即可精确命中，
// 同时与黑色（默认文字）/ 白色色标拉开足够距离，不会误伤。
const CHANNEL_TOLERANCE = 0.06;

// 纯黑用更小容差精确命中 [0,0,0]，避开近黑装饰色（如 0.094 那档），只改图标主体。
const BLACK_TOLERANCE = 0.02;

// 门洞灰用小容差精确命中，避开白色(1)与近黑装饰色，只改 door 主体填充。
const DOOR_TOLERANCE = 0.03;

type NormalizedRgb = [number, number, number];

interface JsonObject {
  [key: string]: unknown;
}

function isJsonObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * 把任意 CSS 颜色字符串（hex / rgb / hsl / 具名）解析成归一化 RGB。
 * 借浏览器计算样式统一格式，避免自己解析各种颜色写法。
 */
function parseCssColorToNormalizedRgb(value: string): NormalizedRgb | null {
  if (typeof document === "undefined" || !value) return null;

  const probe = document.createElement("span");
  probe.style.color = value;
  probe.style.display = "none";
  document.body.appendChild(probe);
  const computed = getComputedStyle(probe).color;
  document.body.removeChild(probe);

  const match = computed.match(/rgba?\(([^)]+)\)/);
  if (!match) return null;

  const parts = match[1].split(",").map((part) => Number.parseFloat(part.trim()));
  if (parts.length < 3 || parts.some((part) => Number.isNaN(part))) return null;

  return [parts[0] / 255, parts[1] / 255, parts[2] / 255];
}

/**
 * 读取当前主题的 --color-brand-primary 并转成归一化 RGB。
 * 读不到（SSR / 变量缺失 / 解析失败）时返回 null，调用方原样使用未着色数据。
 */
export function getBrandPrimaryRgb(): NormalizedRgb | null {
  if (typeof document === "undefined") return null;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--color-brand-primary")
    .trim();
  return parseCssColorToNormalizedRgb(raw);
}

/** 读取当前主题的未选中图标色 --color-text-secondary（对齐未选中 label），转归一化 RGB。 */
export function getInactiveIconRgb(): NormalizedRgb | null {
  if (typeof document === "undefined") return null;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--color-text-secondary")
    .trim();
  return parseCssColorToNormalizedRgb(raw);
}

/**
 * 读取当前主题的导航底色 --color-bg-secondary，转归一化 RGB。
 * 兄弟图标（Rewards/Invite…）的内部细节线是「镂空露出底色」而非画上去的颜色；
 * home 的门要与之一致，就得填成底色、看起来像同样露出背景的开口（换肤时同步）。
 */
export function getNavBgRgb(): NormalizedRgb | null {
  if (typeof document === "undefined") return null;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--color-bg-secondary")
    .trim();
  return parseCssColorToNormalizedRgb(raw);
}

function isBakedTeal(r: number, g: number, b: number): boolean {
  return (
    Math.abs(r - BAKED_TEAL[0]) <= CHANNEL_TOLERANCE &&
    Math.abs(g - BAKED_TEAL[1]) <= CHANNEL_TOLERANCE &&
    Math.abs(b - BAKED_TEAL[2]) <= CHANNEL_TOLERANCE
  );
}

function isBakedBlack(r: number, g: number, b: number): boolean {
  return (
    Math.abs(r - BAKED_BLACK[0]) <= BLACK_TOLERANCE &&
    Math.abs(g - BAKED_BLACK[1]) <= BLACK_TOLERANCE &&
    Math.abs(b - BAKED_BLACK[2]) <= BLACK_TOLERANCE
  );
}

function isBakedDoor(r: number, g: number, b: number): boolean {
  return (
    Math.abs(r - BAKED_DOOR[0]) <= DOOR_TOLERANCE &&
    Math.abs(g - BAKED_DOOR[1]) <= DOOR_TOLERANCE &&
    Math.abs(b - BAKED_DOOR[2]) <= DOOR_TOLERANCE
  );
}

/**
 * 就地改写一段扁平的 Lottie 渐变色标数组。
 * 结构：前 stopCount*4 个数是颜色段 [pos, r, g, b] × stopCount，
 * 之后（若有）是透明度段 [pos, alpha] × n。只在颜色段内改 r/g/b，位置与透明度段一律不动。
 * teal 色标→brand（选中态高亮）；纯黑色标→inactive（未选中态图标色，深套下不隐形）。
 * teal 与 black 在同一 gf 的不同关键帧上（frame0 黑→末帧 teal），二者互不重叠。
 */
function recolorFlatGradient(
  flat: number[],
  stopCount: number,
  brand: NormalizedRgb,
  inactive: NormalizedRgb | null
): void {
  const colorSegmentLength = stopCount * 4;
  if (flat.length < colorSegmentLength) return;

  for (let i = 0; i < stopCount; i += 1) {
    const base = i * 4;
    const r = flat[base + 1];
    const g = flat[base + 2];
    const b = flat[base + 3];
    if (isBakedTeal(r, g, b)) {
      flat[base + 1] = brand[0];
      flat[base + 2] = brand[1];
      flat[base + 3] = brand[2];
    } else if (inactive && isBakedBlack(r, g, b)) {
      flat[base + 1] = inactive[0];
      flat[base + 2] = inactive[1];
      flat[base + 3] = inactive[2];
    }
  }
}

function isFlatNumberArray(value: unknown): value is number[] {
  return Array.isArray(value) && value.every((item) => typeof item === "number");
}

/** 处理单个渐变填充节点（ty === "gf"）的颜色数据，兼容静态与动画关键帧两种写法。 */
function recolorGradientFillNode(
  node: JsonObject,
  brand: NormalizedRgb,
  inactive: NormalizedRgb | null
): void {
  const gradient = node.g;
  if (!isJsonObject(gradient)) return;

  const stopCount = gradient.p;
  if (typeof stopCount !== "number") return;

  const keyframeHolder = gradient.k;
  if (!isJsonObject(keyframeHolder)) return;

  const values = keyframeHolder.k;
  if (isFlatNumberArray(values)) {
    // 静态渐变（a:0）：k 直接是扁平色标数组。
    recolorFlatGradient(values, stopCount, brand, inactive);
    return;
  }

  if (Array.isArray(values)) {
    // 动画渐变（a:1）：k 是关键帧数组，每帧的 s（起始值）是扁平色标数组。
    for (const frame of values) {
      if (isJsonObject(frame) && isFlatNumberArray(frame.s)) {
        recolorFlatGradient(frame.s, stopCount, brand, inactive);
      }
    }
  }
}

/**
 * 就地改写单组实心填充的 r/g/b 三分量（透明度尾段不动）。
 * 纯黑→inactive（未选中图标色）；门洞灰→doorCutout（导航底色，读作镂空）。二者互斥，不重叠。
 */
function recolorSolidTriple(
  s: number[],
  inactive: NormalizedRgb | null,
  doorCutout: NormalizedRgb | null
): void {
  if (s.length < 3) return;
  if (inactive && isBakedBlack(s[0], s[1], s[2])) {
    s[0] = inactive[0];
    s[1] = inactive[1];
    s[2] = inactive[2];
  } else if (doorCutout && isBakedDoor(s[0], s[1], s[2])) {
    s[0] = doorCutout[0];
    s[1] = doorCutout[1];
    s[2] = doorCutout[2];
  }
}

/**
 * 处理单个实心填充节点（ty === "fl"）：纯黑→未选中图标色，门洞灰→导航底色，
 * 兼容静态与关键帧两种写法。
 */
function recolorSolidFillNode(
  node: JsonObject,
  inactive: NormalizedRgb | null,
  doorCutout: NormalizedRgb | null
): void {
  const color = node.c;
  if (!isJsonObject(color)) return;

  const values = color.k;
  if (isFlatNumberArray(values)) {
    // 静态色（a:0）：k = [r,g,b(,a?)]。
    recolorSolidTriple(values, inactive, doorCutout);
    return;
  }

  if (Array.isArray(values)) {
    // 动画色（a:1）：k = 关键帧数组，每帧 s = [r,g,b(,a?)]。
    for (const frame of values) {
      if (isJsonObject(frame) && isFlatNumberArray(frame.s)) {
        recolorSolidTriple(frame.s, inactive, doorCutout);
      }
    }
  }
}

/**
 * 递归遍历动画数据并着色：
 * 渐变填充(gf) 的 teal→brand；实心填充(fl) 的纯黑→inactive、门洞灰→doorCutout。
 */
function walkAndRecolor(
  node: unknown,
  brand: NormalizedRgb,
  inactive: NormalizedRgb | null,
  doorCutout: NormalizedRgb | null
): void {
  if (Array.isArray(node)) {
    for (const child of node) {
      walkAndRecolor(child, brand, inactive, doorCutout);
    }
    return;
  }
  if (!isJsonObject(node)) return;

  if (node.ty === "gf") {
    recolorGradientFillNode(node, brand, inactive);
  } else if (node.ty === "fl" && (inactive || doorCutout)) {
    recolorSolidFillNode(node, inactive, doorCutout);
  }

  for (const value of Object.values(node)) {
    if (typeof value === "object" && value !== null) {
      walkAndRecolor(value, brand, inactive, doorCutout);
    }
  }
}

/**
 * 返回一份按当前主题着色后的 Lottie 数据副本（深拷贝，绝不改动入参）。
 * 拿不到主题主色时原样返回原对象，保证退化行为安全。
 * teal 渐变→brand（选中态高亮）；纯黑实心填充→未选中图标色（深套下黑图标不隐形）；
 * home 门洞灰→导航底色（--color-bg-secondary），让门读作「露出背景的开口」，与
 * Rewards/Invite 等兄弟图标的镂空细节线同一表现（换肤同步、深浅主题都跟底色走）。
 */
export function applyBrandThemeToLottie<T extends object>(data: T): T {
  const brand = getBrandPrimaryRgb();
  if (!brand) return data;

  const inactive = getInactiveIconRgb();
  // 门填成纯导航底色：兄弟图标的细节线本就是镂空露底色，门用底色即与之一致。
  const doorCutout = getNavBgRgb();
  const clone = JSON.parse(JSON.stringify(data)) as T;
  walkAndRecolor(clone, brand, inactive, doorCutout);
  return clone;
}
